import {
  LitElement,
  html,
  css,
  unsafeHTML,
  utils,
  paths,
  t,
  broadcastReceiver,
} from '@hh/global.js'

class HHSizeChartModal extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
    },
  }

  constructor() {
    super()
  }

  requestUpdate(name, oldValue) {
    if (name === 'open' && !!this[name]) {
      // TODO: not sure if this is the best behaviour, but givem that all the videos are meant to be muted and just loop, I think I'd rather have them "running" in the background while a modal is open than worry about restarting them
      // utils.pauseAllMedia()
    }

    return super.requestUpdate(name, oldValue)
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('modal', (...args) => {
      // when passing the data attribute from the <hh-broadcast> component
      // id needs to be first and action second; action is optional
      const { id = null, action = null } = args[0]
      if (!action || action === 'open') this.openFromDocument(...args)
      if (action === 'close') this.closeFromDocument(...args)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver.disconnect()
  }

  initCloseOnEscape(event) {
    if (event.code.toUpperCase() === 'ESCAPE' && this.open) {
      this.open = false
      document.documentElement.classList.remove('overflow-hidden')
    }
  }

  openFromDocument(data) {
    if (this.id === data.id) {
      this.open = true
      document.documentElement.classList.add('overflow-hidden')
      document.addEventListener('keydown', (event) =>
        this.initCloseOnEscape(event)
      )
    } else {
      this.open = false
      return
    }

    if (data.element) {
      setTimeout(() => {
        this.querySelector(`[id="${data.element}"]`)?.scrollIntoView()
      }, 0)
    }
  }

  closeFromDocument(data) {
    if (this.id === data.id) {
      const event = { type: 'click' } // fake a js event obj
      this.close(event)
      document.removeEventListener('keydown', (event) =>
        this.initCloseOnEscape(event)
      )
    }
  }

  close(event) {
    if (event.type === 'click') {
      document.documentElement.classList.remove('overflow-hidden')
      this.open = false
      return
    }
  }

  render() {
    return html`<div
      @keyup="${this.close}"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
    >
      <div
        class="content"
        role="document"
        aria-label="${t('products.modal.label')}"
        tabindex="0"
      >
        <hh-focus-trap active="${this.open}">
          <div class="wrapper">
            <button
              @click="${this.close}"
              class="button size-chart-close-btn"
              type="button"
              aria-label="${t('accessibility.close')}"
              part="close-btn"
            >
              ${unsafeHTML(paths.close)}
            </button>
            <slot></slot>
          </div>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    :host {
      background-color: var(--hh-color-background);
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      top: 0;
      width: 100%;
      visibility: hidden;
      z-index: -1;
    }

    :host([size='flyout']) {
      max-width: var(--hh-max-width, 400px);
      height: auto;
      top: var(--hh-flyout-offset, 30px);
      right: var(--hh-flyout-offset, 30px);
      left: revert;
      border: 0.1rem solid var(--hh-border-color);
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    .button {
      aspect-ratio: 1;
      top: 0;
      right: 0;
      width: 5rem;
      height: 5rem;
      position: absolute;
      display: flex;
      background-color: var(--hh-background);
      border: 0.1rem solid var(--hh-border-color);
      border-radius: 50%;
      color: var(--hh-color);
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 2;
    }

    .button svg {
      width: 10px;
    }

    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    .wrapper {
      position: relative;
      background-color: var(--hh-color-background);
      padding: 15px;
      padding-right: 0;
    }

    .content {
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: auto;
      width: 100%;
    }

    :host([size='flyout']) .content {
      height: auto;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }

    @media screen and (min-width: 750px) {
      :host {
        background-color: unset;
        padding: var(--hh-space-md) var(--hh-space-page-margins);
      }

      .content {
        justify-content: center;
        align-items: center;
      }

      .wrapper {
        width: 65vw;
        height: 65vh;
        padding: var(--hh-space-md) var(--hh-space-page-margins);
      }

      :host([open='true']) {
        background-color: rgba(0, 0, 0, 0.5);
      }
    }
  `
}

customElements.define('hh-size-chart-modal', HHSizeChartModal)
